import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';

import SuccessModal from '../../components/global/SuccessModal';
import styles from './loginForm.module.css';
import OtpInput from '../../components/global/OtpInput';
import { Box, Flex, Img, Text, Tooltip } from '@chakra-ui/react';
import Typography from '../../components_/Typography';
import { arrowLeft } from '../../assets/images/login';
import { Colors } from '../../utils/constants';
import { ClipLoader } from 'react-spinners';
import Button from '../../components_/button';
import customToast, { ToastType } from '../../components_/Toast';
import { useGetAuthOtp, useVerifyEmailOtp } from '../../apis/auth';
import { encryptData } from '../../utils/encryptJson';
import { OtpPhonesIcons } from '../../assets/icons';
import { wqkgx } from '../../config/keys';
import { decryptJson } from '../../utils/decryptJson';
import Countdown from './OtpCountdown';

interface IProps {
  hasBack?: boolean;
  userEmail?: string;
  loginToken?: string;
  handleSuccess?: any;
}
const OtpForm = ({ hasBack = true, userEmail = '', loginToken = '', handleSuccess }: IProps) => {
  const [otpValues, setOtpValues] = useState<string[]>(new Array(6).fill(''));
  const [isCountdownActive, setIsCountdownActive] = useState(true);
  const {
    mutate: getAuthOtp,
    isPending: getAuthOtpLoading,
    data: otpData,
    isError: getOtpError,
  } = useGetAuthOtp();
  const { mutate: verifyEmailOtp, isPending: verifyEmailOtpLoading } = useVerifyEmailOtp();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams?.get('source') ?? userEmail ?? '';
  const token = queryParams?.get('token') ?? loginToken ?? '';
  const otpMessage =
    otpData?.status === 200
      ? otpData?.message
      : getAuthOtpLoading
      ? 'Getting otp...'
      : getOtpError
      ? 'An error occured while getting the OTP'
      : 'Please enter the six digit verification code sent to your email';
  return (
    <Box className={styles.formContainer}>
      {hasBack && (
        <Flex
          mb={'2rem'}
          alignItems={'center'}
          gap={'8px'}
          cursor={'pointer'}
          onClick={() => navigate(`/new-password?token=${token}&source=${email}`)}
        >
          <Img src={arrowLeft} boxSize={{ base: '1rem', md: '1.5rem' }} />
          <Typography color={Colors.green} fontSize={'0.7rem'} fontWeight={500}>
            Back
          </Typography>
        </Flex>
      )}
      <Box mb={'2rem'}>
        <Typography color={Colors.black} fontSize={'1.125rem'} fontWeight={700} mb={'1rem'}>
          OTP Verification
        </Typography>
        <Typography color={Colors.black} fontSize={'0.75rem'} fontWeight={400} mb={'1.5rem'}>
          {otpMessage}
        </Typography>
      </Box>
      <Flex my={'1rem'} justifyContent={'center'}>
        <Img src={OtpPhonesIcons} height={'120px'} width={'213px'} />
      </Flex>
      <OtpInput otp={otpValues} setOtp={setOtpValues} />
      <Button
        width={'100%'}
        py={{ base: '0.75rem', md: '1rem' }}
        height={{ base: '2.5rem', md: '2.875rem' }}
        mt={'1.5rem'}
        text={'VERIFY OTP'}
        type='submit'
        isLoading={verifyEmailOtpLoading}
        zIndex={hasBack ? 1000 : 0}
        position={'relative'}
        onClick={() => {
          verifyEmailOtp(
            encryptData({
              email,
              otp: CryptoJS.AES.encrypt(otpValues?.join(''), wqkgx).toString(),
            }),
            {
              onError: (err: Error) =>
                customToast(err.message || 'Could not verify otp', ToastType.error),
              onSuccess: (res) => {
                handleSuccess
                  ? handleSuccess(res)
                  : navigate(`/set-security-questions?token=${token}&source=${email}`);
              },
            },
          );
        }}
      />

      <Flex
        mt={'16px'}
        gap={'4px'}
        justifyContent={'space-between'}
        zIndex={hasBack ? 1000 : 0}
        position={'relative'}
      >
        <Flex gap={'4px'}>
          <Text
            fontSize={'10px'}
            fontFamily={'Poppins'}
            lineHeight={'15px'}
            fontWeight={400}
            color={Colors.black}
          >
            {`Didn't get the code?`}
          </Text>
          <Tooltip
            placement='top'
            label='You can only request for another otp after the countdown runs down'
            isDisabled={!isCountdownActive}
            fontSize={'12px'}
            fontWeight={400}
            fontFamily={'Poppins'}
            closeOnClick={false}
          >
            <Text
              fontSize={'10px'}
              fontFamily={'Poppins'}
              lineHeight={'15px'}
              fontWeight={500}
              color={Colors.green}
              cursor={getAuthOtpLoading ? 'not-allowed' : 'pointer'}
              _hover={{
                textDecorationLine: 'underline',
              }}
              onClick={() => {
                if (!getAuthOtpLoading && !isCountdownActive) {
                  getAuthOtp(encryptData({ email }), {
                    onError: (err: Error) => {
                      customToast(
                        typeof err?.message === 'string' ? err?.message : 'Failed to resend otp',
                        ToastType.error,
                      );
                    },
                    onSuccess: () => {
                      customToast('OTP re-sent successfully', ToastType.success);
                      setIsCountdownActive(true);
                    },
                  });
                  setOtpValues(new Array(6).fill(''));
                }
              }}
            >
              {getAuthOtpLoading && (
                <ClipLoader size={'12px'} className='mr-1' color={Colors.green} />
              )}
              {getAuthOtpLoading ? 'Resending otp' : `Resend otp`}
            </Text>
          </Tooltip>
        </Flex>
        {isCountdownActive && (
          <Countdown
            isCountdownActive={isCountdownActive}
            setIsCountdownActive={setIsCountdownActive}
          />
        )}
      </Flex>
    </Box>
  );
};

export default OtpForm;
